/* KycSectionOwnershipComponents/SignatoriesTable.jsx */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "../../../../components/ui/Modal";
import CountrySelect from "../../../../components/ui/CountrySelect";
import {
  normalizeSignatoryCapacity,
  SIGNATORY_CAPACITY_OPTIONS,
} from "../signatoryCapacity";

/**
 * SignatoriesTable
 * - Pure table for ownership.controllers (renamed logically to "signatories" in UI)
 * - Keeps stable _rowId for each row (adds if missing)
 * - Allows add/delete/update
 *
 * Parent should pass:
 *  - controllers (array)
 *  - readOnly
 *  - onSetAt (path array, value)
 *  - changedWrapClass (optional string)
 *  - title / hint / addLabel / emptyLabel (optional)
 */
export default function SignatoriesTable({
  readOnly = false,
  signatories = [],
  onSetAt,

  wrapClassName = "",

  title = "Authorised signatories",
  hint = "Any person legally authorised to act or sign on behalf of the company.",
  addLabel = "Add signatory",
  emptyLabel = "No signatories added yet.",
}) {
  const CAPACITY_OPTIONS = useMemo(
    () => [{ v: "", label: "Select" }].concat(
      SIGNATORY_CAPACITY_OPTIONS.map((opt) => ({
        v: opt.value,
        label: opt.label,
      }))
    ),
    []
  );

  function safeClone(obj) {
    try {
      if (typeof structuredClone === "function") return structuredClone(obj);
    } catch (_) {}
    return JSON.parse(JSON.stringify(obj ?? {}));
  }

  function makeRowId(prefix = "row") {
    return `${prefix}_${Date.now().toString(36)}_${Math.random()
      .toString(36)
      .slice(2, 10)}`;
  }

  function ensureRowIds(list) {
    if (readOnly) return;
    if (!Array.isArray(list) || list.length === 0) return;

    let changed = false;
    const existingIds = new Set(
      list
        .map((r, idx) => String(r?._rowId || `sig_idx_${idx}`))
        .filter(Boolean)
    );
    const next = list.map((r) => {
      const row = r && typeof r === "object" ? r : {};
      const normalizedCapacity = normalizeSignatoryCapacity(row?.capacity);
      const rowId = row._rowId || makeRowId("sig");
      const normalizedSignatureMode = row.signatureMode === "joint" ? "joint" : "sole";
      const normalizedJointWith = Array.isArray(row.jointWithSignatoryIds)
        ? row.jointWithSignatoryIds
            .map((id) => String(id || ""))
            .filter((id) => id && id !== rowId && existingIds.has(id))
        : [];
      if (
        !row._rowId ||
        normalizedCapacity !== String(row?.capacity || "") ||
        normalizedSignatureMode !== String(row?.signatureMode || "sole") ||
        JSON.stringify(normalizedJointWith) !== JSON.stringify(row?.jointWithSignatoryIds || [])
      ) {
        changed = true;
        return {
          ...row,
          _rowId: rowId,
          capacity: normalizedCapacity,
          signatureMode: normalizedSignatureMode,
          jointWithSignatoryIds: normalizedSignatureMode === "joint" ? normalizedJointWith : [],
        };
      }
      return row;
    });

    if (changed) onSetAt?.(["ownership", "signatories"], next);
  }

  // ensure ids (depend on signature so it runs when missing ids appear)
  const signature = useMemo(() => {
    return (Array.isArray(signatories) ? signatories : [])
      .map((c, idx) => String(c?._rowId || `missing_${idx}`))
      .join("|");
  }, [signatories]);

  useEffect(() => {
    ensureRowIds(signatories);
    // eslint-disable-next-line
  }, [readOnly, signature]);

  function updateRow(idx, key, value) {
    if (readOnly) return;
    const list = safeClone(signatories || []);
    if (!list[idx]) list[idx] = {};
    if (key === "capacity") {
      list[idx][key] = normalizeSignatoryCapacity(value);
    } else if (key === "signatureMode") {
      list[idx].signatureMode = value === "joint" ? "joint" : "sole";
      if (list[idx].signatureMode !== "joint") list[idx].jointWithSignatoryIds = [];
    } else if (key === "jointWithSignatoryIds") {
      const rowId = String(list[idx]?._rowId || "");
      const allowedIds = new Set(
        list
          .map((row, rowIdx) => String(row?._rowId || `sig_idx_${rowIdx}`))
          .filter((id) => id && id !== rowId)
      );
      list[idx].jointWithSignatoryIds = Array.isArray(value)
        ? value.map((id) => String(id || "")).filter((id) => allowedIds.has(id))
        : [];
      list[idx].signatureMode = "joint";
    } else {
      list[idx][key] = value;
    }
    onSetAt?.(["ownership", "signatories"], list);
  }

  function addRow() {
    if (readOnly) return;
    const list = safeClone(signatories || []);
    list.push({
      _rowId: makeRowId("sig"),
      name: "",
      capacity: "",
      nationality: "",
      countryOfResidence: "",
      signatureMode: "sole",
      jointWithSignatoryIds: [],
    });
    onSetAt?.(["ownership", "signatories"], list);
  }

  // delete with tiny delay for smoothness (optional)
  const [deleting, setDeleting] = useState({});
  const timersRef = useRef({});
  const [jointPicker, setJointPicker] = useState(null);

  const jointPickerRow =
    jointPicker && Number.isInteger(jointPicker.idx) ? signatories[jointPicker.idx] : null;
  const jointPickerRowId = jointPickerRow
    ? String(jointPickerRow?._rowId || `sig_idx_${jointPicker.idx}`)
    : "";
  const jointPickerOptions = jointPicker
    ? signatories
        .map((row, rowIdx) => ({
          id: String(row?._rowId || `sig_idx_${rowIdx}`),
          name: String(row?.name || "").trim() || `Signatory ${rowIdx + 1}`,
        }))
        .filter((option) => option.id && option.id !== jointPickerRowId)
    : [];
  const selectedJointIds = Array.isArray(jointPickerRow?.jointWithSignatoryIds)
    ? jointPickerRow.jointWithSignatoryIds.map((id) => String(id))
    : [];

  function handleSignatureModeChange(idx, value) {
    updateRow(idx, "signatureMode", value);
    if (value === "joint") setJointPicker({ idx });
    else setJointPicker(null);
  }

  function toggleJointSignatory(id) {
    if (!jointPicker || !id) return;
    const current = new Set(selectedJointIds);
    if (current.has(id)) current.delete(id);
    else current.add(id);
    updateRow(jointPicker.idx, "jointWithSignatoryIds", Array.from(current));
  }

  function requestDelete(rowId) {
    if (readOnly) return;
    if (!rowId) return;

    setDeleting((p) => ({ ...(p || {}), [rowId]: true }));

    const key = String(rowId);
    if (timersRef.current[key]) clearTimeout(timersRef.current[key]);

    timersRef.current[key] = setTimeout(() => {
      const list = safeClone(signatories || []);
      const next = list.filter(
        (r) => String(r?._rowId || "") !== String(rowId)
      ).map((row) => ({
        ...row,
        jointWithSignatoryIds: Array.isArray(row?.jointWithSignatoryIds)
          ? row.jointWithSignatoryIds.filter((id) => String(id) !== String(rowId))
          : [],
      }));
      onSetAt?.(["ownership", "signatories"], next);

      setDeleting((p) => {
        const n = { ...(p || {}) };
        delete n[key];
        return n;
      });

      delete timersRef.current[key];
    }, 160);
  }

  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach((t) => clearTimeout(t));
      timersRef.current = {};
    };
  }, []);

  const TrashBtn = ({ onClick, disabled }) => (
    <button
      type="button"
      className="btn btn-sm btn-delete-trash p-0 d-inline-flex align-items-center justify-content-center"
      aria-label="Remove row"
      title="Remove row"
      onClick={onClick}
      disabled={disabled}
    >
      <i className="bi bi-trash" />
    </button>
  );

  const countrySelectLikeInputClass = "w-100 countryLikeInput";

  return (
    <>
      {/* <hr className="opacity-25" style={{ marginTop: 25, marginBottom: 20 }} /> */}

      <div className="d-flex justify-content-between align-items-end mb-2">
        <div>
          <div className="OwnershipMainSectionTitle">{title}</div>
          {hint ? (
            <div className="text-muted kycOwnSmallNote">{hint}</div>
          ) : null}
        </div>

        {!readOnly ? (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={addRow}
          >
            <i className="bi bi-plus-lg me-2" aria-hidden="true"></i>
            {addLabel}
          </button>
        ) : null}
      </div>

      {/* <div
        className={"table-responsive kycTableWrap " + (changedWrapClass || "")}
      > */}
      <div className={"table-responsive kycTableWrap " + (wrapClassName || "")}>
        <table className="table table-sm align-middle kycTbl kycTblControllers">
          <colgroup>
            <col className="c-name" />
            <col className="c-capacity" />
            <col className="c-nat" />
            <col className="c-res" />
            {!readOnly ? <col className="c-actions" /> : null}
          </colgroup>

          <thead>
            <tr>
              <th>Name</th>
              <th>Capacity</th>
              <th>Nationality</th>
              <th>Country of residence</th>
              {!readOnly ? <th className="kycTblActionsTh" /> : null}
            </tr>
          </thead>

          <tbody>
            {signatories.length === 0 ? (
              <tr>
                <td colSpan={readOnly ? 4 : 5} className="text-muted">
                  {emptyLabel}
                </td>
              </tr>
            ) : (
              signatories.map((r, idx) => {
                const rowId = r?._rowId || `sig_idx_${idx}`;
                const isDeleting = !!deleting?.[rowId];
                return (
                  <tr
                    key={rowId}
                    style={
                      isDeleting
                        ? { pointerEvents: "none", opacity: 0.5 }
                        : undefined
                    }
                  >
                    <td>
                      <input
                        className="form-control form-control-sm"
                        value={r?.name ?? ""}
                        disabled={readOnly || isDeleting}
                        onChange={(e) => updateRow(idx, "name", e.target.value)}
                      />
                    </td>

                    <td>
                      <div className="kycSigTableStackedControls">
                        <select
                          className="form-select form-select-sm"
                          value={r?.capacity ?? ""}
                          disabled={readOnly || isDeleting}
                          onChange={(e) =>
                            updateRow(idx, "capacity", e.target.value)
                          }
                        >
                          <option value="">Capacity</option>
                          {CAPACITY_OPTIONS.filter((o) => o.v).map((o) => (
                            <option key={o.v} value={o.v}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                        <select
                          className="form-select form-select-sm"
                          value={r?.signatureMode === "joint" ? "joint" : "sole"}
                          disabled={readOnly || isDeleting}
                          onChange={(e) =>
                            handleSignatureModeChange(idx, e.target.value)
                          }
                        >
                          <option value="sole">Sole</option>
                          <option value="joint">Joint</option>
                        </select>
                      </div>
                    </td>

                    <td className="align-middle">
                      <CountrySelect
                        value={r?.nationality ?? ""}
                        onChange={(val) =>
                          updateRow(idx, "nationality", val || "")
                        }
                        className={countrySelectLikeInputClass}
                        optionTextClassName="countryTextOptionsOwnership"
                        disabled={readOnly || isDeleting}
                      />
                    </td>

                    <td className="align-middle">
                      <CountrySelect
                        value={r?.countryOfResidence ?? ""}
                        onChange={(val) =>
                          updateRow(idx, "countryOfResidence", val || "")
                        }
                        className={countrySelectLikeInputClass}
                        optionTextClassName="countryTextOptionsOwnership"
                        disabled={readOnly || isDeleting}
                      />
                    </td>

                    {!readOnly ? (
                      <td className="kycTblActionsTd">
                        <TrashBtn
                          disabled={isDeleting}
                          onClick={() => requestDelete(rowId)}
                        />
                      </td>
                    ) : null}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <Modal
        open={!!jointPicker}
        title="Joint signatories"
        onClose={() => setJointPicker(null)}
        className="kycSignatoryJointModal"
        footer={
          <>
            <button type="button" className="btn btn-modal-cancel" onClick={() => setJointPicker(null)}>
              Cancel
            </button>
            <button type="button" className="btn btn-modal-save" onClick={() => setJointPicker(null)}>
              Done
            </button>
          </>
        }
      >
        {jointPickerOptions.length ? (
          <div className="kycSigJointModalList">
            {jointPickerOptions.map((option) => (
              <label key={option.id} className="kycSigJointModalOption">
                <input
                  type="checkbox"
                  checked={selectedJointIds.includes(option.id)}
                  onChange={() => toggleJointSignatory(option.id)}
                />
                <span>{option.name}</span>
              </label>
            ))}
          </div>
        ) : (
          <div className="kycSigJointModalEmpty">Add another signatory first.</div>
        )}
      </Modal>
    </>
  );
}
